import { apiClient } from './client'
import { ApiError } from './problem'
import type { BrokerView, BuyerView, TransferView } from '../types/monetization'

/**
 * `GET /api/v1/credit-instruments/{instrumentId}/transfer` (GA demo v4) — a thin, additive read
 * this story wires onto the already-published `monetization.api.TransferReadApi
 * .findByInstrumentId` port (`TransferController`, ADR-0042 §3.2). Returns `null` (never throws)
 * on a `404` — "not yet monetized" is a legitimate, common state, not a fetch error; any other
 * failure still propagates as `ApiError`.
 */
export async function getTransferByInstrument(
  instrumentId: string,
  signal?: AbortSignal,
): Promise<TransferView | null> {
  try {
    const { data } = await apiClient.get<TransferView>(
      `/credit-instruments/${instrumentId}/transfer`,
      { signal },
    )
    return data
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null
    }
    throw err
  }
}

/** `GET /api/v1/buyers` — an unpaginated array (`BuyerController.list`, mirrors `film.ts`'s
 * `listVendors`). Used to resolve `TransferView.buyerId` to a real name (CLAUDE.md §4 IA — never
 * a raw UUID), never to write. */
export async function listBuyers(signal?: AbortSignal): Promise<BuyerView[]> {
  const { data } = await apiClient.get<BuyerView[]>('/buyers', { signal })
  return data
}

/** `GET /api/v1/brokers` — an unpaginated array (`BrokerController.list`). */
export async function listBrokers(signal?: AbortSignal): Promise<BrokerView[]> {
  const { data } = await apiClient.get<BrokerView[]>('/brokers', { signal })
  return data
}
